/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  PlanTaskActualNodeInfoVehicle,
  ResultTaskListMultipleCharge,
  TasksApi,
} from "client";
import { Const } from "const";
import { useApiWithTimeout } from "../../../hooks/ApiWrapperWithTimeout";
import { useCallback } from "react";

export interface MultipleChargeTasksItem {
  jobsetId: string;
  jobsetName: string;
  startNodeId: number;
  endNodeId: number;
  startDate: Date;
  endDate: Date;
  memo: string;
  chargerIds: string[];
  tasks: TaskItem[];
  moveToStart: boolean;
  chargeOutsideDelivery: boolean;
  resetJobRequestedEndDatetime: boolean;
  versionId: string;
}
export interface DateTimeRange {
  from: Date;
  to: Date;
}
export interface TaskItem {
  taskId: number;
  frameTypeNumber: string;
  jobs: JobItem[];
  actualRootInfo: PlanTaskActualNodeInfoVehicle[];
}

export interface JobItem {
  id: number;
  status: number;
  unloadingUserId: string;
  unloadingNodeId: number;
  extensionTimeUnload: number;
  loadingUserId: string;
  loadingNodeId: number;
  extensionTimeLoad: number;
  delayFlag: boolean;
  planningStartDatetime: Date;
  planningEndDatetime: Date | undefined;
  materialId: number;
  materialQuantity: number;
  requestedEndDateTime: DateTimeRange;
  receiveStatusUpdatedDatetime: Date;
}
export const useRegisteredMultipleChargeTasks = () => {
  const [api, canceler] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );

  const getMultipleChargeTasks = useCallback(
    async (jobsetId: string) => {
      // Retrieve registered jobset
      const responseGet = await api.getTaskMultipleCharge({
        jobSetId: jobsetId,
      });

      if (
        responseGet.jobsetInfos !== undefined &&
        responseGet.jobsetInfos.length > 0
      ) {
        return convertResponseToViewItem(responseGet.jobsetInfos[0]);
      }
    },
    [api]
  );

  return { getMultipleChargeTasks, canceler };
};

/**
 * Convert responsu object to view object
 *
 * @param {ResultTaskListMultipleCharge} response response object
 * @return {MultipleChargeTasksItem} View object
 */
const convertResponseToViewItem = (response: ResultTaskListMultipleCharge) => {
  const viewItem: MultipleChargeTasksItem = {
    jobsetId: response.jobSetId,
    jobsetName: response.jobSetName,
    startNodeId: response.startNodeId,
    endNodeId: response.endNodeId,
    startDate: response.jobsetStartDatetime,
    endDate: response.jobsetEndDatetime,
    memo: response.memo,
    chargerIds: response.chargerIds,
    tasks: response.taskInfos.map((taskInfo) => {
      return {
        taskId: taskInfo.taskId,
        frameTypeNumber: taskInfo.frameTypeAndNumber,
        actualRootInfo: taskInfo.taskActualRouteInfo ?? [],
        jobs: taskInfo.jobInfos.map((jobInfo) => {
          return {
            id: jobInfo.jobId,
            status: 0,
            unloadingUserId: "",
            unloadingNodeId: jobInfo.unloadingNodeId,
            extensionTimeUnload: 0,
            loadingUserId: "",
            loadingNodeId: jobInfo.loadingNodeId,
            extensionTimeLoad: 0,
            delayFlag: false,
            planningStartDatetime: new Date(),
            planningEndDatetime: jobInfo.jobPlanningEndDatetime,
            materialId: jobInfo.loadingMaterialId,
            materialQuantity: jobInfo.loadingQuantity,
            requestedEndDateTime: {
              from: new Date(),
              to: new Date(),
            },
            receiveStatusUpdatedDatetime: new Date(),
          } as JobItem;
        }),
      };
    }),
    chargeOutsideDelivery: response.chargeOutsideDelivery,
    moveToStart: false,
    resetJobRequestedEndDatetime: false,
    versionId: response.versionId,
  };
  return viewItem;
};
